import React from 'react';
import { Provider } from 'urql';
import { uniswapV3Client } from './services/graphqlClient';
import { Dashboard } from './components/Dashboard';
import './App.css';

// Professional Error Boundary Component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Dashboard Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ 
          padding: 'var(--wp--preset--spacing--80)', 
          textAlign: 'center',
          background: 'linear-gradient(135deg, var(--wp--preset--color--black) 0%, #1a1a1a 50%, var(--wp--preset--color--black) 100%)',
          color: 'var(--text-inverse)',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'var(--font-family-primary)'
        }}>
          <div style={{
            background: 'var(--bg-card)',
            padding: 'var(--wp--preset--spacing--80)',
            borderRadius: 'var(--border-radius-lg)',
            boxShadow: 'var(--wp--preset--shadow--deep)',
            maxWidth: '600px',
            width: '100%'
          }}>
            <h1 style={{
              fontSize: 'var(--wp--preset--font-size--x-large)',
              fontWeight: 'var(--font-weight-extrabold)',
              margin: '0 0 var(--wp--preset--spacing--50) 0',
              background: 'var(--wp--preset--gradient--luminous-vivid-amber-to-luminous-vivid-orange)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              EverValue Liquidity Dashboard
            </h1>
            <h2 style={{
              fontSize: 'var(--wp--preset--font-size--large)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--color-error)',
              margin: '0 0 var(--wp--preset--spacing--50) 0'
            }}>
              ⚠️ Error Loading Dashboard
            </h2>
            <p style={{
              fontSize: 'var(--wp--preset--font-size--medium)',
              color: 'var(--text-secondary)',
              margin: '0 0 var(--wp--preset--spacing--40) 0',
              lineHeight: 'var(--line-height-normal)'
            }}>
              There was an error loading the dashboard components.
            </p>
            <p style={{
              fontSize: 'var(--wp--preset--font-size--small)',
              color: 'var(--text-tertiary)',
              margin: '0 0 var(--wp--preset--spacing--60) 0',
              fontFamily: 'var(--font-family-mono)',
              background: 'var(--bg-secondary)',
              padding: 'var(--wp--preset--spacing--40)',
              borderRadius: 'var(--border-radius-md)',
              border: '1px solid var(--border-light)'
            }}>
              Error: {this.state.error?.message}
            </p>
            <button 
              onClick={() => window.location.reload()}
              style={{
                padding: 'var(--wp--preset--spacing--50) var(--wp--preset--spacing--70)',
                fontSize: 'var(--wp--preset--font-size--medium)',
                fontWeight: 'var(--font-weight-semibold)',
                background: 'var(--wp--preset--gradient--luminous-vivid-amber-to-luminous-vivid-orange)',
                color: 'var(--text-inverse)',
                border: 'none',
                borderRadius: 'var(--border-radius-md)',
                cursor: 'pointer',
                transition: 'all var(--transition-normal)',
                boxShadow: 'var(--wp--preset--shadow--natural)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = 'var(--wp--preset--shadow--deep)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'var(--wp--preset--shadow--natural)';
              }}
            >
              🔄 Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

function App() {
  return (
    <div className="App">
      <Provider value={uniswapV3Client}>
        <ErrorBoundary>
          <Dashboard />
        </ErrorBoundary>
      </Provider>
    </div>
  );
}

export default App;